/**
 * The two visuals under the sole trader calculator (T916): where the business income goes, and the same pay as an
 * ABN contractor and as an employee. The bar is aria-hidden; the legend and "Show as a table" repeat every number, so
 * nothing depends on the picture or on colour (each part has its own pattern and a card-coloured gap). Patterns come
 * from the pay calculator charts. Updates fade in on opacity/transform only, off under prefers-reduced-motion.
 */
import { add, money, subtract, type Money } from "@stb/core/money"
import type { TakeHomeResult } from "@stb/jurisdiction-au/income-tax"
import { ChartColumn, ChevronDown, Scale } from "lucide-react"

import type { Locale } from "@/core/i18n/locale"
import { useLocale } from "@/core/i18n/locale-context"

import { SOLE_VISUAL_COPY_ZH } from "../../content/zh/sole-trader-calculator-copy"
import { CalcHeading, calcVisualCard } from "./calc-ui"
import { formatAud } from "./calculator"
import { ChartPatterns, FillKeySwatch, patternId, type FillKey } from "./pay-calculator-charts"
import type { SoleSharePart, SoleShareKey } from "./sole-trader-calculator-model"

/** Text of the two visuals, by language. */
export interface SoleVisualCopy {
  labels: Record<SoleShareKey, string>
  splitHeading: string
  showTable: string
  splitCaption: string
  part: string
  amount: string
  share: string
  compareHeading: string
  compareCaption: string
  abn: string
  employee: string
  rowBefore: string
  rowExpenses: string
  rowTax: string
  rowTakeHome: string
  rowSuper: string
  note: string
}

const VISUAL_COPY_EN: SoleVisualCopy = {
  labels: {
    takeHome: "Take-home",
    expenses: "Business expenses",
    tax: "Income tax",
    medicare: "Medicare levy",
    help: "HELP repayment",
  },
  splitHeading: "Where your business income goes",
  showTable: "Show as a table",
  splitCaption: "Where your business income goes, for the year",
  part: "Part",
  amount: "Amount",
  share: "Share",
  compareHeading: "ABN contractor or employee on the same pay",
  compareCaption:
    "Take-home on an ABN compared with an employee earning the same amount, for the year",
  abn: "ABN (you)",
  employee: "Employee",
  rowBefore: "Pay before tax",
  rowExpenses: "Business expenses",
  rowTax: "Income tax and levies",
  rowTakeHome: "Take-home",
  rowSuper: "Employer super, paid on top",
  note: "The employee column treats your ABN income as a salary, with no super in it and no work expenses. Paid leave and other employment entitlements are not valued, so the comparison is about tax and super only.",
}

const VISUAL_COPY: Record<Locale, SoleVisualCopy> = { en: VISUAL_COPY_EN, zh: SOLE_VISUAL_COPY_ZH }

/** Expenses reuse the dashed grey pattern the pay calculator draws for super. */
const FILL: Record<SoleShareKey, FillKey> = {
  takeHome: "takeHome",
  expenses: "super",
  tax: "tax",
  medicare: "medicare",
  help: "help",
}

const percent = (bp: number) =>
  `${new Intl.NumberFormat("en-AU", { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(bp / 100)}%`
const DASH = "–"
const fade = (animate: boolean) =>
  animate ? "animate-[pc-fade-in_200ms_cubic-bezier(.2,.8,.2,1)] motion-reduce:animate-none" : ""

/** Percentage attribute for the SVG; display geometry only. */
const p = (bp: number) => `${(Math.max(0, Math.min(10_000, bp)) / 100).toFixed(3)}%`

export function SoleSplitVisual({
  id,
  parts,
  income,
  animate,
}: {
  id: string
  /** Null when the form is invalid. */
  parts: SoleSharePart[] | null
  income: Money | null
  animate: boolean
}) {
  const t = VISUAL_COPY[useLocale()]
  const visible = parts?.filter((s) => s.amount.amount > 0 || s.key === "takeHome") ?? []
  let x = 0
  const segments = visible.flatMap((s) => {
    const seg = { key: s.key, x, w: s.shareBp }
    x += s.shareBp
    return s.shareBp > 0 ? [seg] : []
  })
  return (
    <figure className={calcVisualCard}>
      <figcaption>
        <CalcHeading icon={ChartColumn} tone="primary">
          {t.splitHeading}
        </CalcHeading>
      </figcaption>
      <ChartPatterns base={id} />
      <svg
        aria-hidden="true"
        data-testid="sole-split-bar"
        width="100%"
        height="24"
        className="block h-6 w-full overflow-visible"
      >
        <rect width="100%" height="24" rx="4" className="fill-muted" />
        <g key={income?.amount ?? "none"} className={`${fade(animate)} origin-left`}>
          {segments.map((s) => (
            <rect
              key={s.key}
              x={p(s.x)}
              width={p(s.w)}
              height="24"
              rx="3"
              fill={`url(#${patternId(id, FILL[s.key])})`}
              className="stroke-card"
              strokeWidth="2"
            />
          ))}
        </g>
      </svg>
      <ul className="grid gap-2 text-[15px] sm:grid-cols-2">
        {visible.map((s) => (
          <li key={s.key} className="flex items-center gap-2.5">
            <FillKeySwatch base={id} fill={FILL[s.key]} />
            <span className="grow">{t.labels[s.key]}</span>
            <span className="tabular-nums">{formatAud(s.amount)}</span>
            <span className="text-muted-foreground w-14 text-right text-sm tabular-nums">
              {percent(s.shareBp)}
            </span>
          </li>
        ))}
        {!parts && <li className="text-muted-foreground">{DASH}</li>}
      </ul>
      <details className="group">
        <summary className="text-primary focus-visible:ring-ring flex min-h-11 w-fit cursor-pointer list-none items-center gap-1.5 rounded-md text-sm font-medium outline-none focus-visible:ring-[3px] [&::-webkit-details-marker]:hidden">
          <ChevronDown
            className="size-4 transition-transform group-open:rotate-180 motion-reduce:transition-none"
            aria-hidden="true"
          />
          {t.showTable}
        </summary>
        <table className="mt-1 w-full text-[15px]">
          <caption className="sr-only">{t.splitCaption}</caption>
          <thead>
            <tr className="text-muted-foreground border-border border-b text-left text-sm">
              <th scope="col" className="py-2 font-medium">
                {t.part}
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                {t.amount}
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                {t.share}
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => (
              <tr key={s.key} className="border-border border-b last:border-b-0">
                <th scope="row" className="py-2 text-left font-normal">
                  {t.labels[s.key]}
                </th>
                <td className="py-2 text-right tabular-nums">{formatAud(s.amount)}</td>
                <td className="py-2 text-right tabular-nums">{percent(s.shareBp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  )
}

/** Income tax after offsets, Medicare levy and HELP, for the year, as an employee. */
const employeeTax = (e: TakeHomeResult["annual"]) =>
  add(add(subtract(e.incomeTax, e.lito), e.medicareLevy), e.studyLoanRepayment)

export function SoleCompareVisual({
  income,
  expenses,
  businessTax,
  takeHome,
  employee,
}: {
  income: Money | null
  expenses: Money | null
  businessTax: Money | null
  takeHome: Money | null
  employee: TakeHomeResult | null
}) {
  const t = VISUAL_COPY[useLocale()]
  const e = employee?.annual
  const show = (m: Money | null | undefined) => (m ? formatAud(m) : DASH)
  const rows: [string, string, string, boolean][] = [
    [t.rowBefore, show(income), show(e?.gross), false],
    [t.rowExpenses, expenses ? `−${formatAud(expenses)}` : DASH, DASH, false],
    [t.rowTax, show(businessTax), show(e && employeeTax(e)), false],
    [t.rowTakeHome, show(takeHome), show(e?.takeHome), true],
    [t.rowSuper, formatAud(money(0, "AUD")), show(e?.employerSuper), false],
  ]
  return (
    <figure className={calcVisualCard}>
      <figcaption>
        <CalcHeading icon={Scale} tone="success">
          {t.compareHeading}
        </CalcHeading>
      </figcaption>
      <table className="w-full text-[15px]">
        <caption className="sr-only">{t.compareCaption}</caption>
        <thead>
          <tr className="text-muted-foreground border-border border-b text-left text-sm">
            <td className="py-2" />
            <th scope="col" className="py-2 text-right font-medium">
              {t.abn}
            </th>
            <th scope="col" className="py-2 text-right font-medium">
              {t.employee}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, abn, emp, strong]) => (
            <tr key={label} className="border-border border-b last:border-b-0">
              <th
                scope="row"
                className={`py-2.5 text-left ${strong ? "font-bold" : "font-normal"}`}
              >
                {label}
              </th>
              <td className={`py-2.5 text-right tabular-nums ${strong ? "font-bold" : ""}`}>
                {abn}
              </td>
              <td className={`py-2.5 text-right tabular-nums ${strong ? "font-bold" : ""}`}>
                {emp}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-muted-foreground max-w-[70ch] text-sm">{t.note}</p>
    </figure>
  )
}
